import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FlagImage } from 'react-international-phone';
import { i18n } from '../../localization/i18n';
import { countriescodes } from '../../Helpers/countries';
import { OutsideClickHandler } from '../../Helpers/Utility';
import { IconCountryChevron } from '../../Components/Common/Icons';

const COUNTRY_SEARCH_MAX_LENGTH = 255;
const COUNTRY_RESULT_LIMIT = 60;

/**
 * Country flag.
 *
 * Uses the sprite from react-international-phone rather than the flag emoji,
 * which Windows renders as the two letters of the country code.
 *
 * @param {string} code Two-letter ISO country code.
 */
export const Flag = ({ code }) => <FlagImage iso2={code.toLowerCase()} size="24px" />;

Flag.propTypes = { code: PropTypes.string.isRequired };

const CountryPicker = ({ countryCode, dialCode, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const searchRef = useRef(null);

    useEffect(() => {
        if (open) searchRef.current?.focus();
    }, [open]);

    const results = useMemo(() => {
        const needle = keyword.trim().toLowerCase();
        const matches = needle
            ? countriescodes.filter(
                  // Name matches loosely; the dial code has to match from the start.
                  country => country.name.toLowerCase().includes(needle) || country.dial_code.startsWith(needle),
              )
            : countriescodes;
        return matches.slice(0, COUNTRY_RESULT_LIMIT);
    }, [keyword]);

    const handleToggle = () => {
        setOpen(prev => !prev);
        setKeyword('');
    };

    const handlePick = country => {
        onSelect({ countryCode: country.code, dialCode: country.dial_code });
        setOpen(false);
    };

    // The overlay only exists while the menu is open, otherwise it would swallow
    // clicks meant for the rest of the form.
    return (
        <OutsideClickHandler showOverlay={open} onOutsideClick={() => setOpen(false)}>
            <div className={`country${open ? ' country-open' : ''}`}>
                <button
                    type="button"
                    className="cbtn"
                    onClick={handleToggle}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={i18n.t('register.COUNTRY_ARIA')}
                >
                    <Flag code={countryCode} />
                    <span>{dialCode}</span>
                    <IconCountryChevron />
                </button>

                {open && (
                    <div className="menu" role="listbox">
                        <div className="msearch">
                            <input
                                ref={searchRef}
                                type="search"
                                placeholder={i18n.t('register.COUNTRY_SEARCH_PLACEHOLDER')}
                                maxLength={COUNTRY_SEARCH_MAX_LENGTH}
                                value={keyword}
                                onChange={event => setKeyword(event.target.value)}
                                aria-label={i18n.t('register.COUNTRY_SEARCH_ARIA')}
                            />
                        </div>

                        {results.length === 0 ? (
                            <p className="mempty">{i18n.t('register.COUNTRY_NO_RESULTS')}</p>
                        ) : (
                            results.map(country => (
                                <button
                                    key={country.code}
                                    type="button"
                                    role="option"
                                    aria-checked={country.code === countryCode}
                                    aria-selected={country.code === countryCode}
                                    className="mitem"
                                    onClick={() => handlePick(country)}
                                >
                                    <Flag code={country.code} />
                                    <span>{country.name}</span>
                                    <span className="dial">{country.dial_code}</span>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </OutsideClickHandler>
    );
};

CountryPicker.propTypes = {
    countryCode: PropTypes.string.isRequired,
    dialCode: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
};

export default CountryPicker;
